import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import Footer from '../components/Footer';
import api from '../api/axios';

const Login = () => {
    // Función para limpiar siempre la caché de imágenes
    const [cacheBuster] = useState(() => Date.now());
    const getCleanImageUrl = (url) => {
        if (!url) return '';
        return `${url}${url.includes('?') ? '&' : '?'}v=${cacheBuster}`;
    };

    const { login } = useAuth();
    const { config, loading: configLoading } = useConfig();
    const [loginInput, setLoginInput] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [viewMode, setViewMode] = useState('landing'); // 'landing' or 'login'
    const navigate = useNavigate();

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        const cleanInput = (loginInput || '').replace(/\D/g, '');
        if (!cleanInput) {
            setError('Por favor ingresa un número de Colegio Médico válido (solo números).');
            setIsLoading(false);
            return;
        }
        const result = await login(cleanInput);
        if (result.success) {
            try {
                await api.post('/sessions/log', {
                    page_name: 'login',
                    action_type: 'login'
                });
            } catch (err) {
                console.error("Failed to log login action", err);
            }
            const pendingCity = sessionStorage.getItem('pending_attendance_city') || sessionStorage.getItem('pending_email_attendance_city');
            const isAutoConfirm = sessionStorage.getItem('pending_attendance_auto_confirm') === 'true' || sessionStorage.getItem('pending_email_attendance') === 'true';

            if (pendingCity || isAutoConfirm) {
                try {
                    await api.post('/attendance/self-confirm', {
                        city: pendingCity || null,
                        method: 'url_link',
                        record_type: 'asistire_al_evento'
                    });
                } catch (e) {}
                sessionStorage.removeItem('pending_attendance_city');
                sessionStorage.removeItem('pending_email_attendance_city');
                sessionStorage.removeItem('pending_attendance_auto_confirm');
                sessionStorage.removeItem('pending_email_attendance');
            }

            navigate('/');
        } else {
            setError('Valide su información, si no se encuentra registrado, lo invitamos a registrarse.');
            setIsLoading(false);
        }
    };

    if (configLoading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="w-10 h-10 border-4 border-slate-200 border-t-[#0a58ca] rounded-full animate-spin"></div>
            </div>
        );
    }

    const pageDesign = config?.pageDesigns?.login || {};
    const primaryColor = config?.primaryColor || "#00595b";
    const secondaryColor = config?.secondaryColor || "#ff5874";
    const tertiaryColor = config?.tertiaryColor || "#008b8b";
    const cardBorderColor = config?.cardBorderColor || secondaryColor;
    const backgroundColor = config?.backgroundColor || "#00484c";

    const customLogo = isMobile
        ? (pageDesign?.headerTitleUrl || pageDesign?.headerLogoUrl || config?.logoMobileUrl || config?.logoUrl || config?.headerLogoUrl || config?.headerTitleUrl)
        : (pageDesign?.headerTitleUrl || pageDesign?.headerLogoUrl || config?.logoUrl || config?.logoMobileUrl || config?.headerLogoUrl || config?.headerTitleUrl);

    const landingLoginBtnText = pageDesign.landing_login_btn || "Iniciar sesión";
    const landingRegisterBtnText = pageDesign.landing_register_btn || "Registrarse";
    const loginTitle = pageDesign.title || "ACCESO";
    const loginSubtitle = pageDesign.subtitle || "Ingresa tus datos";
    const identificationLabel = config?.registration_fields?.identification_label || "Número de Colegio Médico";
    const identificationPlaceholder = config?.registration_fields?.identification_placeholder || "Ejemplo: 123456";
    const loginButtonText = pageDesign.login_button_text || "Ingresar";
    const backLinkText = pageDesign.back_link_text || "← Volver";

    const pageBgMobile = pageDesign?.bgImageMobile || pageDesign?.bgImage;
    const pageBgDesktop = pageDesign?.bgImage || pageDesign?.bgImageMobile;
    const globalBgMobile = config?.backgroundMobile || config?.backgroundDesktop || config?.backgroundImage;
    const globalBgDesktop = config?.backgroundDesktop || config?.backgroundImage || config?.backgroundMobile;

    const bgImageUrl = isMobile
        ? (pageBgMobile || globalBgMobile)
        : (pageBgDesktop || globalBgDesktop);

    const containerStyle = {
        backgroundColor: backgroundColor,
        color: pageDesign.globalFontColor || '#ffffff',
        backgroundImage: bgImageUrl ? `url(${getCleanImageUrl(bgImageUrl)})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
    };

    return (
        <div
            style={containerStyle}
            className="min-h-screen flex flex-col relative overflow-x-hidden font-sans items-center justify-center p-4"
        >
            {/* Header Logo Banner */}
            {customLogo && (
                <div className="relative z-50 w-full max-w-sm flex justify-center mb-6 pointer-events-none">
                    <img
                        src={getCleanImageUrl(customLogo)}
                        alt="Logo"
                        className="w-full h-auto max-h-28 object-contain drop-shadow-md"
                    />
                </div>
            )}

            {/* Content Area */}
            <div className="w-full max-w-sm px-4 z-10 flex flex-col justify-center animate-fadeIn">
                {viewMode === 'landing' && (
                    <div className="flex flex-col gap-4 w-full">
                        <button
                            onClick={() => setViewMode('login')}
                            style={{ backgroundColor: tertiaryColor }}
                            className="w-full text-white py-4 px-6 rounded-2xl font-black uppercase tracking-widest text-[14px] sm:text-[15px] shadow-lg hover:scale-[1.02] active:scale-95 transition-all text-center"
                        >
                            {landingLoginBtnText}
                        </button>

                        <button
                            onClick={() => navigate('/register')}
                            style={{ backgroundColor: tertiaryColor }}
                            className="w-full text-white py-4 px-6 rounded-2xl font-black uppercase tracking-widest text-[14px] sm:text-[15px] shadow-lg hover:scale-[1.02] active:scale-95 transition-all text-center"
                        >
                            {landingRegisterBtnText}
                        </button>
                    </div>
                )}

                {viewMode === 'login' && (
                    <div
                        style={{ borderColor: cardBorderColor }}
                        className="w-full bg-white p-8 rounded-3xl border-2 shadow-2xl transition-all"
                    >
                        <div className="text-center mb-6">
                            <h3 style={{ color: primaryColor }} className="text-2xl font-black uppercase tracking-tighter">{loginTitle}</h3>
                            <p className="text-xs text-slate-500 uppercase tracking-widest mt-1">{loginSubtitle}</p>
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div className="space-y-2">
                                <label
                                    style={{ color: secondaryColor }}
                                    className="text-[11px] uppercase font-black tracking-wide pl-1 block text-left"
                                >
                                    {identificationLabel}
                                </label>
                                <input
                                    type="tel"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    className="w-full bg-white border-2 border-slate-200 focus:border-[#ff5874] p-4 rounded-xl focus:outline-none transition-all font-bold text-slate-800 text-center shadow-inner placeholder:text-slate-300 text-sm"
                                    placeholder={identificationPlaceholder}
                                    value={loginInput}
                                    onChange={(e) => setLoginInput(e.target.value.replace(/\D/g, ''))}
                                    required
                                />
                            </div>

                            <button
                                type="submit"
                                style={{ backgroundColor: secondaryColor }}
                                className="w-full text-white py-4 rounded-xl font-black uppercase tracking-widest text-[13px] shadow-lg hover:scale-[1.02] active:scale-95 transition-all mt-4"
                            >
                                {isLoading ? 'Accediendo...' : loginButtonText}
                            </button>

                            {error && (
                                <div className="space-y-3 mt-4">
                                    <div className="bg-red-50 text-red-600 p-3 rounded-lg text-[10px] font-black uppercase tracking-wider border border-red-200 text-center">
                                        {error}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => navigate('/register')}
                                        style={{ backgroundColor: secondaryColor }}
                                        className="w-full text-white py-3 px-6 rounded-xl font-black uppercase tracking-widest text-[13px] shadow-lg hover:scale-[1.02] active:scale-95 transition-all text-center"
                                    >
                                        REGISTRARSE
                                    </button>
                                </div>
                            )}

                            <button
                                type="button"
                                onClick={() => setViewMode('landing')}
                                className="w-full text-slate-400 text-[10px] font-black uppercase tracking-widest hover:text-slate-800 transition-colors pt-2 block text-center"
                            >
                                {backLinkText}
                            </button>
                        </form>
                    </div>
                )}
            </div>

            {/* Footer Brand Logo */}
            <Footer />
        </div>
    );
};

export default Login;
